/**
 * ChipGroup
 * Classification: custom
 *
 * Layout and overflow for a collection of chips. Generalises the capping logic
 * that was inlined in `TableChipsCell` so a filter bar, a card body and a table
 * cell all wrap, space and truncate a group the same way.
 *
 * The numbers below come from Carbon's tag group guidance, which is the most
 * specific published advice on the subject: 8px between chips, six or fewer on
 * one line, and wrapping to at most five lines before the pattern is wrong and
 * the content wants a multi-select or a table instead.
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import { Chip, type ChipSize } from '@/atoms/Chip';

export interface ChipGroupProps {
  /** The chips. Anything falsy is dropped so conditional children are safe. */
  children: React.ReactNode;
  /**
   * Maximum chips shown before the rest collapse behind a `+N more` control.
   * Omit to show every chip.
   */
  max?: number;
  /** Spacing between chips, in 8px grid units. */
  spacing?: number;
  /** Prevent wrapping and keep the group on one line. */
  noWrap?: boolean;
  /**
   * Called instead of expanding in place, for when the remainder belongs in a
   * popover or a detail view rather than inline.
   */
  onOverflow?: () => void;
  /** Accessible name for the group, e.g. "MITRE techniques". */
  label?: string;
  /**
   * Size of the overflow `+N more` chip. Children keep their own `size`;
   * pass the same value on both when the group should share a height.
   * @default 'small'
   */
  size?: ChipSize;
}

/**
 * @example
 * <ChipGroup label="Techniques" max={3}>
 *   {techniques.map((t) => <Chip key={t} purpose="category" label={t} size="small" />)}
 * </ChipGroup>
 */
export function ChipGroup({
  children,
  max,
  spacing = 1,
  noWrap = false,
  onOverflow,
  label,
  size = 'small',
}: ChipGroupProps) {
  const [expanded, setExpanded] = useState(false);

  // Flatten so `{cond && <Chip/>}` and arrays both count correctly, otherwise a
  // conditional child would consume a slot in the cap while rendering nothing.
  const items = (Array.isArray(children) ? children.flat(Infinity) : [children]).filter(Boolean);

  const capped = max !== undefined && !expanded && items.length > max;
  const visible = capped ? items.slice(0, max) : items;
  const hidden = items.length - visible.length;

  return (
    <Box
      role={label ? 'group' : undefined}
      aria-label={label}
      sx={{
        display: 'flex',
        flexWrap: noWrap ? 'nowrap' : 'wrap',
        alignItems: 'center',
        gap: spacing,
        minWidth: 0,
      }}
    >
      {visible}
      {hidden > 0 && (
        <Chip
          purpose="overflow"
          count={hidden}
          size={size}
          expanded={expanded}
          onDisclose={() => (onOverflow ? onOverflow() : setExpanded(true))}
        />
      )}
    </Box>
  );
}

export default ChipGroup;
